/**
 * Right-rail panel for the Drills home (desktop only). Surfaces
 * actionable context the user can act on without leaving the page:
 *
 *   - "Currently working on" — 3 weakest mapped skills, each clickable
 *     to drill that specific skill
 *   - "Pick up where you left off" — most recent conversation
 *   - "Today's reading" — rotating study sheet, deterministic per
 *     calendar day so the user sees the same suggestion all day
 *
 * Hidden on screens narrower than `lg` — keeps mobile uncluttered.
 */

import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { fetchSkills, listConversations, listSheets } from "@/shared/api";
import { drillSkillRoute } from "@/shared/chatRoute";
import { getDailyChallenge } from "@/features/daily/lib/dailyChallenge";
import { getGamification } from "@/features/gamification/lib/gamification";
import { DrillLaunchLink } from "@/shared/components/DrillLaunchLink";
import { relativeTime } from "@/shared/format";
import type { SkillState } from "@/shared/models";
import {
  BookOpen,
  ChevronRight,
  Clock,
  Crosshair,
  Dumbbell,
  Flame,
  ICON_STROKE,
  Zap,
} from "@/shared/components/icons";

export function TodayPanel() {
  const skills = useQuery({ queryKey: ["skills"], queryFn: fetchSkills });
  const conversations = useQuery({
    queryKey: ["conversations"],
    queryFn: listConversations,
  });
  const sheets = useQuery({
    queryKey: ["sheets"],
    queryFn: listSheets,
    staleTime: Infinity,
  });
  // Shared with the Drills-home TodaysDrillCard (same query key). Once
  // the daily is completed, its full card vacates the main column and a
  // compact summary lands here in the side pane.
  const daily = useQuery({
    queryKey: ["daily-challenge"],
    queryFn: getDailyChallenge,
    staleTime: 60_000,
  });
  // Personal progress (level + XP + streak). Surfaced here — not the
  // leaderboard — because personal progress is meaningful even with one
  // user, while a near-empty leaderboard would advertise emptiness. Same
  // query the global LevelChip uses.
  const gamification = useQuery({
    queryKey: ["gamification"],
    queryFn: getGamification,
    staleTime: 60_000,
  });

  const weakestThree = (() => {
    const list = skills.data?.skills ?? [];
    return list
      .filter((s) => s.signalCount > 0)
      .sort((a, b) => a.level - b.level)
      .slice(0, 3);
  })();

  const recent = (conversations.data ?? [])[0];

  // Deterministic daily rotation through the 15 sheets — same user
  // sees the same suggestion all day, but it changes overnight.
  const todaysSheet = (() => {
    const list = sheets.data ?? [];
    if (list.length === 0) return null;
    const dayIndex = Math.floor(Date.now() / 86_400_000);
    return list[dayIndex % list.length];
  })();

  const level = gamification.data;
  const levelPct =
    level && level.levelSpan > 0
      ? Math.min(100, Math.round((level.intoLevel / level.levelSpan) * 100))
      : 0;
  const toNextLevel = level ? Math.max(0, level.levelSpan - level.intoLevel) : 0;
  const streak = daily.data?.currentStreak ?? 0;

  return (
    <aside className="hidden lg:block w-72 flex-shrink-0 space-y-5">
      {level && level.totalXp > 0 && (
        <PanelSection Icon={Zap} title="Your level" iconTint="accent">
          <Link
            to="/skills/level"
            className="block rounded-lg hover:bg-text/[0.04] p-2 -mx-2 transition group"
          >
            <div className="flex items-center justify-between gap-2 mb-2">
              <span className="text-sm text-text font-medium group-hover:text-accent transition">
                Level {level.level}
              </span>
              {streak > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-500/15 border border-amber-500/30 text-amber-300 text-xs font-semibold tabular-nums flex-shrink-0">
                  <Flame size={12} strokeWidth={ICON_STROKE} />
                  {streak}
                </span>
              )}
            </div>
            <div className="h-1.5 bg-border rounded-full overflow-hidden">
              <div
                className="h-full bg-accent"
                style={{ width: `${Math.max(2, levelPct)}%` }}
              />
            </div>
            <div className="flex justify-between text-[10px] text-text-muted tabular-nums mt-1">
              <span>{level.totalXp.toLocaleString()} XP</span>
              <span>{toNextLevel.toLocaleString()} to level {level.level + 1}</span>
            </div>
          </Link>
        </PanelSection>
      )}

      {daily.data?.completedToday && (
        <PanelSection Icon={Crosshair} title="Today's Drill" iconTint="accent">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="text-sm text-text font-medium truncate">
                <span className="text-accent">✓</span> {daily.data.skillName}
              </div>
              <div className="text-[11px] text-text-muted mt-0.5">
                Cleared, back tomorrow
              </div>
            </div>
            {daily.data.currentStreak > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-500/15 border border-amber-500/30 text-amber-300 text-xs font-semibold tabular-nums flex-shrink-0">
                <Flame size={12} strokeWidth={ICON_STROKE} />
                {daily.data.currentStreak}
              </span>
            )}
          </div>
        </PanelSection>
      )}

      {weakestThree.length > 0 && (
        <PanelSection
          Icon={Dumbbell}
          title="Currently working on"
          iconTint="amber"
        >
          <div className="space-y-1.5">
            {weakestThree.map((s) => (
              <WorkingOnRow key={s.skillId} skill={s} />
            ))}
          </div>
        </PanelSection>
      )}

      {recent && (
        <PanelSection
          Icon={Clock}
          title="Pick up where you left off"
          iconTint="accent"
        >
          <Link
            to={`/chat/${recent.id}`}
            className="block rounded-lg hover:bg-text/[0.04] p-2 -mx-2 transition group"
          >
            <div className="text-sm text-text font-medium truncate group-hover:text-accent transition">
              {recent.title || "Untitled"}
            </div>
            <div className="text-[11px] text-text-muted mt-0.5">
              {recent.messageCount} message
              {recent.messageCount === 1 ? "" : "s"} ·{" "}
              {relativeTime(recent.updatedAt)}
            </div>
          </Link>
        </PanelSection>
      )}

      {todaysSheet && (
        <PanelSection
          Icon={BookOpen}
          title="Today's reading"
          iconTint="accent"
        >
          <Link
            to={`/study/${todaysSheet.id}`}
            className="block rounded-lg hover:bg-text/[0.04] p-2 -mx-2 transition group"
          >
            <div className="text-sm text-text font-medium group-hover:text-accent transition">
              {todaysSheet.displayName}
            </div>
            <div className="text-[11px] text-text-muted mt-0.5">
              Sheet {todaysSheet.number.toString().padStart(2, "0")} ·{" "}
              {todaysSheet.category}
            </div>
          </Link>
        </PanelSection>
      )}
    </aside>
  );
}

// ----------------------------------------------------------------------------
// Building blocks
// ----------------------------------------------------------------------------

function PanelSection({
  Icon,
  title,
  iconTint,
  children,
}: {
  Icon: React.ComponentType<{ size?: number; strokeWidth?: number }>;
  title: string;
  iconTint: "amber" | "accent";
  children: React.ReactNode;
}) {
  const iconBg =
    iconTint === "amber"
      ? "bg-amber-500/15 text-amber-400"
      : "bg-accent/15 text-accent";
  return (
    <section>
      <div className="flex items-center gap-2 mb-2.5">
        <span
          className={`w-6 h-6 rounded-md flex items-center justify-center ${iconBg}`}
        >
          <Icon size={13} strokeWidth={ICON_STROKE} />
        </span>
        <h3 className="text-[10px] font-semibold uppercase tracking-[0.15em] text-text-muted/90">
          {title}
        </h3>
      </div>
      <div className="rounded-xl border border-border bg-surface/50 p-3">
        {children}
      </div>
    </section>
  );
}

function WorkingOnRow({ skill }: { skill: SkillState }) {
  const level = Math.max(0, Math.min(100, skill.level));
  const barColor =
    level >= 65
      ? "bg-green-400/70"
      : level >= 40
        ? "bg-yellow-400/70"
        : "bg-red-400/70";

  return (
    <DrillLaunchLink
      to={drillSkillRoute(skill)}
      className="flex items-center gap-2 rounded-lg hover:bg-text/[0.04] p-2 -mx-2 transition group"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2 mb-1">
          <span className="text-xs text-text font-medium group-hover:text-accent transition truncate">
            {skill.name}
          </span>
          <span className="text-[10px] text-text-muted tabular-nums">
            {level}
          </span>
        </div>
        <div className="h-1 bg-border rounded-full overflow-hidden">
          <div
            className={`h-full ${barColor}`}
            style={{ width: `${Math.max(2, level)}%` }}
          />
        </div>
      </div>
      <ChevronRight
        size={12}
        strokeWidth={ICON_STROKE}
        className="text-text-muted/50 group-hover:text-accent group-hover:translate-x-0.5 transition flex-shrink-0"
      />
    </DrillLaunchLink>
  );
}

